import { Controller } from "@hotwired/stimulus"

// Pflege-Rente-Rechner: Rentenbeiträge der Pflegekasse und Rentenplus für
// pflegende Angehörige. Alle Rechengrößen kommen server-seitig als JSON
// (PflegeRenteRechnerController#show), gerechnet wird nur hier.
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "pflegegradButton",
    "pg1Note",
    "leistungsartRadio",
    "pflegeStundenInput",
    "erwerbStundenInput",
    "dauerInput",
    "vollrenteCheckbox",

    "resultPlus",
    "entgelt",
    "beitrag",
    "epJahr",
    "hochrechnungDauer",
    "hochrechnungPlus",
    "hochrechnungEp",
    "verdict",

    "warnErwerb",
    "warnMindest",
    "warnMindestStunden",
    "teilrenteHint",

    "stepProzentsatz",
    "stepPlus",

    "resultSummary",
    "stickyBar",
    "stickyPlus"
  ]

  connect() {
    this.euroFormatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    this.epFormatter = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    this.prozentFormatter = new Intl.NumberFormat("de-DE", { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 2 })
    this.jahreFormatter = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 })
    this.selectPflegegradValue(3)
    this.observeResultCard()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // Phone sticky bar: visible whenever the result summary is off screen (it is lg:hidden on desktop)
  observeResultCard() {
    if (!this.hasStickyBarTarget || !this.hasResultSummaryTarget || !("IntersectionObserver" in window)) return
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting
      this.stickyBarTarget.classList.toggle("translate-y-full", !show)
      this.stickyBarTarget.inert = !show
    })
    this.observer.observe(this.resultSummaryTarget)
  }

  selectPflegegrad(event) {
    this.selectPflegegradValue(parseInt(event.currentTarget.dataset.value))
  }

  selectPflegegradValue(pg) {
    this.selectedPflegegrad = pg

    this.pflegegradButtonTargets.forEach(btn => {
      const isSelected = parseInt(btn.dataset.value) === pg
      btn.setAttribute("aria-pressed", isSelected)
      btn.classList.toggle("bg-ph24_new_blue", isSelected)
      btn.classList.toggle("text-white", isSelected)
      btn.classList.toggle("bg-ph24_new_gray_dark", !isSelected)
      btn.classList.toggle("text-ph24_new_blue", !isSelected)
      btn.classList.toggle("hover:bg-ph24_new_blue/15", !isSelected)
    })

    if (this.hasPg1NoteTarget) this.pg1NoteTarget.classList.toggle("hidden", pg !== 1)

    this.calculate()
  }

  calculate() {
    if (!this.selectedPflegegrad) return
    const data = this.dataValue
    const pg = this.selectedPflegegrad
    const leistungsart = this.leistungsartRadioTargets.find(r => r.checked)?.value || "pflegegeld"

    const pflegeStunden = this.clamp(parseFloat(this.pflegeStundenInputTarget.value), 0, 100, 0)
    const erwerbStunden = this.clamp(parseFloat(this.erwerbStundenInputTarget.value), 0, 100, 0)
    const dauer = this.clamp(parseFloat(this.dauerInputTarget.value), 0.5, 20, 1)

    // Ausschlussgründe: PG 1 und mehr als 30 Wochenstunden Erwerbstätigkeit
    // (genau 30,00 h sind noch zulässig). Unter 10 Pflegestunden nur Hinweis,
    // das Ergebnis wird trotzdem angezeigt.
    const keinePg = pg === 1
    const erwerbZuViel = erwerbStunden > data.max_erwerb_stunden
    const unterMindest = !keinePg && !erwerbZuViel && pflegeStunden < data.min_pflegestunden

    const prozentsatz = keinePg || erwerbZuViel ? 0 : data.prozentsaetze[pg][leistungsart]
    const entgelt = data.bezugsgroesse * prozentsatz
    const beitrag = entgelt * data.beitragssatz
    const epJahr = (entgelt * 12) / data.durchschnittsentgelt
    const plusJahr = epJahr * data.rentenwert
    const epGesamt = epJahr * dauer
    const plusGesamt = plusJahr * dauer

    this.warnErwerbTarget.classList.toggle("hidden", !erwerbZuViel)
    this.warnMindestTarget.classList.toggle("hidden", !unterMindest)
    if (unterMindest) this.warnMindestStundenTarget.textContent = this.jahreFormatter.format(pflegeStunden)
    const vollrente = this.vollrenteCheckboxTarget.checked
    this.teilrenteHintTarget.classList.toggle("hidden", !vollrente)

    this.resultPlusTarget.textContent = this.euroFormatter.format(plusJahr)
    this.entgeltTarget.textContent = this.euroFormatter.format(entgelt)
    this.beitragTarget.textContent = this.euroFormatter.format(beitrag)
    this.epJahrTarget.textContent = this.epFormatter.format(epJahr)
    this.stepProzentsatzTarget.textContent = this.prozentFormatter.format(prozentsatz)
    this.stepPlusTarget.textContent = this.euroFormatter.format(plusJahr)

    this.hochrechnungDauerTarget.textContent = this.formatJahre(dauer)
    this.hochrechnungPlusTarget.textContent = this.euroFormatter.format(plusGesamt)
    this.hochrechnungEpTarget.textContent = this.epFormatter.format(epGesamt)
    if (this.hasStickyPlusTarget) this.stickyPlusTarget.textContent = this.euroFormatter.format(plusJahr)

    if (keinePg) {
      this.verdictTarget.textContent = "Bei Pflegegrad 1 zahlt die Pflegekasse keine Rentenbeiträge."
    } else if (erwerbZuViel) {
      this.verdictTarget.textContent = `Bei mehr als ${data.max_erwerb_stunden} Wochenstunden Erwerbstätigkeit zahlt die Pflegekasse keine Rentenbeiträge.`
    } else if (vollrente) {
      // Zahlen bleiben stehen: sie zeigen, was eine Teilrente (99,99 %) bringen würde, siehe teilrenteHint
      this.verdictTarget.textContent = `Bei einer Vollrente wegen Alters nach der Regelaltersgrenze zahlt die Pflegekasse keine Rentenbeiträge. Mit einer Teilrente wären es ${this.euroFormatter.format(beitrag)} pro Monat.`
    } else if (unterMindest) {
      this.verdictTarget.textContent = `Ab ${data.min_pflegestunden} Pflegestunden pro Woche würde die Pflegekasse ${this.euroFormatter.format(beitrag)} Rentenbeitrag pro Monat zahlen. Sie selbst zahlen nichts ein.`
    } else {
      this.verdictTarget.textContent = `Die Pflegekasse zahlt für Sie ${this.euroFormatter.format(beitrag)} Rentenbeitrag pro Monat. Sie selbst zahlen nichts ein.`
    }

    this.highlightTableCell(keinePg || erwerbZuViel ? null : `${pg}-${leistungsart}`)
  }

  // The Prozentsatz table sits outside this controller; mark the cell the result is based on
  highlightTableCell(key) {
    document.querySelectorAll("[data-pflege-rente-zelle]").forEach(cell => {
      cell.classList.toggle("bg-ph24_new_blue/10", cell.dataset.pflegeRenteZelle === key)
    })
  }

  clamp(value, min, max, fallback) {
    if (isNaN(value)) return fallback
    return Math.min(max, Math.max(min, value))
  }

  formatJahre(dauer) {
    return `${this.jahreFormatter.format(dauer)} ${dauer === 1 ? "Jahr" : "Jahren"}`
  }
}
